import i18n from '@/i18n';
import { store } from '@/App/store'; 

export interface NoviopusEventPayload {
  event: string;
  location_continent: string;
  location_country: string;
  location_local: string;
  user_language: string;
  [key: string]: any; 
}

// ==========================================
// GLOBAL GEO-TRACKING INITIALIZER
// ==========================================
export const initializeGeoCookie = async () => {
  if (typeof document !== 'undefined' && !document.cookie.includes('noviopus_geo=')) {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data && data.country_code) {
        const geoData = {
          countryCode: data.country_code, 
          state: data.region || ''        
        };
        const domain = window.location.hostname.includes('noviopus.com') ? 'domain=.noviopus.com;' : '';
        document.cookie = `noviopus_geo=${encodeURIComponent(JSON.stringify(geoData))}; ${domain} path=/; max-age=2592000`;
      }
    } catch (error) {
      console.warn("Geo-IP fetch failed.");
    }
  }
};

// --- NOVIOPUS TAXONOMY ENGINE ---
const getContinent = (countryCode: string): string => {
  const code = (countryCode || '').toUpperCase();
  if (!code || code === 'UNKNOWN') return 'Unknown'; 

  const middleEast = ['IL', 'AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'YE', 'IQ', 'JO', 'LB', 'SY', 'IR'];
  const northAmerica = ['US', 'CA', 'MX', 'BM', 'BS', 'BZ', 'CR', 'CU', 'DO', 'SV', 'GT', 'HT', 'HN', 'JM', 'NI', 'PA', 'PR'];
  const southAmerica = ['AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'GY', 'PY', 'PE', 'SR', 'UY', 'VE'];
  const australia = ['AU', 'NZ', 'FJ', 'PG', 'SB', 'VU'];
  const europe = ['GB', 'FR', 'DE', 'IT', 'ES', 'UA', 'PL', 'RO', 'NL', 'BE', 'GR', 'CZ', 'PT', 'SE', 'HU', 'BY', 'AT', 'CH', 'RS', 'BG', 'DK', 'FI', 'SK', 'NO', 'IE', 'HR', 'MD', 'BA', 'AL', 'LT', 'SI', 'LV', 'EE', 'ME', 'LU', 'MT', 'IS'];
  const africa = ['NG', 'ET', 'EG', 'CD', 'ZA', 'TZ', 'KE', 'UG', 'DZ', 'SD', 'MA', 'AO', 'MZ', 'GH', 'MG', 'CM', 'CI', 'ZW', 'ML', 'SN', 'TD', 'SO', 'RW', 'GN', 'BJ', 'BI', 'TN', 'SS', 'TG', 'SL', 'LY', 'LR', 'CF', 'MR', 'ER', 'NA', 'GM', 'BW', 'GA', 'LS', 'GW', 'KM', 'CV', 'ST', 'SC'];

  if (middleEast.includes(code)) return 'Middle East';
  if (northAmerica.includes(code)) return 'North America';
  if (southAmerica.includes(code)) return 'South America';
  if (australia.includes(code)) return 'Australia';
  if (europe.includes(code)) return 'Europe';
  if (africa.includes(code)) return 'Africa';
  return 'Asia'; 
};

const getLocal = (countryCode: string, stateName: string): string => {
  const code = (countryCode || '').toUpperCase();
  const allowedLocals = ['US', 'CN', 'IN', 'RU'];
  return allowedLocals.includes(code) && stateName ? stateName : '';
};
// --------------------------------

const getFallbackLocation = () => {
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(/(?:^|; )noviopus_geo=([^;]*)/);
    if (match) {
      try { return JSON.parse(decodeURIComponent(match[1])); } catch (e) {}
    }
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const browserLang = typeof navigator !== 'undefined' ? navigator.language : '';
    const countryCode = browserLang.includes('-') ? browserLang.split('-')[1] : 'Unknown';
    const stateGuess = tz.includes('/') ? tz.split('/')[1].replace('_', ' ') : 'Unknown';
    
    return { countryCode: countryCode.toUpperCase(), state: stateGuess };
  } catch (e) {
    return { countryCode: 'Unknown', state: 'Unknown' };
  }
};

export const trackNoviopusEvent = (
  eventName: string, 
  eventData: Record<string, any> = {}
) => {
  if (typeof window === 'undefined') return;

  let locDetails: any = {};
  let userEmail = '';
  try {
    const state = store.getState();
    const account = state?.auth?.currentAccount || {};
    
    // FIX: Using exact path from Business App schemas
    const loc = account.location || account.locationDetails || {};
    locDetails = loc.details || loc;
    userEmail = state?.auth?.currentUser?.email || account.contactEmail || '';
  } catch (e) {}

  // ==========================================
  // INTERNAL TEAM FILTER (Production Only)
  // ==========================================
  const emailCheck = (eventData.internal_email_check || userEmail || '').toLowerCase().trim();
  delete eventData.internal_email_check;

  const isProductionHost = /^(www\.)?(career\.|business\.)?noviopus\.com$/.test(window.location.hostname);

  if (isProductionHost && emailCheck.endsWith('@noviopus.com')) {
    console.log(`[Telemetry] Internal team member detected (${emailCheck}). Tracking aborted for: ${eventName}`);
    return;
  }
  // ==========================================

  const fallback = getFallbackLocation();

  // FIX: Prioritize 2-letter codes. Redux Code -> Cookie Code -> Timezone Code -> Full Country Name
  const rawCountryCode = (locDetails.countryCode || locDetails.country_code || fallback.countryCode || locDetails.country || '').toUpperCase();
  const rawState = locDetails.state || locDetails.region || fallback.state || '';

  const payload: NoviopusEventPayload = {
    event: eventName,
    location_continent: getContinent(rawCountryCode),
    location_country: rawCountryCode && rawCountryCode !== 'UNKNOWN' ? rawCountryCode : 'Unknown',
    location_local: getLocal(rawCountryCode, rawState),
    user_language: i18n.language || 'en',
    ...eventData
  };

  const win = window as any;
  win.dataLayer = win.dataLayer || [];
  win.dataLayer.push(payload);

  if (process.env.NODE_ENV !== 'production') {
    console.log(`[Telemetry Event]: ${eventName}`, payload);
  }
};